import React, { useEffect, useState, useCallback, useRef } from "react";
import { GetState, GetCity } from "react-country-state-city";
import { useDispatch } from "react-redux";
import "./TransportForm.css";
import Icons from "../../../../Config/Images";
import { UploadMediaRequestAPI } from "../../../../Store/Action/mediaAction";
import { addTransportAPI, updateTransportAPI } from "../../../../Store/Action/transportAction";

const TransporterForm = ({ actionForm }) => {
  const dispatch = useDispatch();
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null); // State to track the index of the row being edited
  const [formData, setFormData] = useState({
    transportName: "",
    headOfficeCity: "",
    serviceNumber: "",
    mobileNumber: "",
    altMobileNumber: "",
    selectedState: "",
    selectedStateName: "",
    selectedCities: [],
    gstin: "",
    gstCertificates: {},
    serviceNumberCertificateFile: "",
    visitingCardFile: "",
  });
  const [tableData, setTableData] = useState([]); // State to hold table data
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const countryId = 101; // India is selected by default

  // Fetch the list of states on component mount
  useEffect(() => {
    if (countryId) {
      GetState(countryId).then(setStateList).catch(console.log);
    }
  }, [countryId]);

  // Fetch cities whenever selectedState changes
  useEffect(() => {
    if (formData.selectedState) {
      GetCity(countryId, formData.selectedState)
        .then(setCityList)
        .catch(console.log);
    } else {
      setCityList([]);
    }
  }, [formData.selectedState, countryId]);

  // Handle input change for text inputs and dropdowns
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  // Handle state change
  const handleStateChange = useCallback(
    (e) => {
      const selectedStateId = parseInt(e.target.value);
      const selectedState = stateList.find(
        (state) => state.id === selectedStateId
      );
      setFormData((prevData) => ({
        ...prevData,
        selectedState: selectedStateId,
        selectedStateName: selectedState ? selectedState.name : "",
        selectedCities: [], // Reset selected cities when state changes
      }));
    },
    [stateList]
  );

  // Handle city checkbox change
  const handleCityCheckboxChange = useCallback((cityId, cityName) => {
    setFormData((prevData) => {
      const updatedCities = prevData.selectedCities.some(
        (city) => city.id === cityId
      )
        ? prevData.selectedCities.filter((city) => city.id !== cityId) // Uncheck city
        : [...prevData.selectedCities, { id: cityId, name: cityName }]; // Check city with both id and name
      return { ...prevData, selectedCities: updatedCities };
    });
  }, []);

  const toggleCityDropdown = () => {
    setIsCityDropdownOpen((prev) => !prev);
  };

  // Handle file change for GST certificates
  const handleFileChangeState = (e, state) => {
    const selectedFile = e.target.files[0];
    dispatch(
      UploadMediaRequestAPI(
        [selectedFile],
        "transportDocuments",
        false,
        (result) => {
          if (result.status) {
            setFormData((prevData) => ({
              ...prevData,
              gstCertificates: {
                ...prevData.gstCertificates,
                [state]: result.data[0].file_storage_id,
              },
            }));
          } else {
            alert("File upload failed. Please try again.");
          }
        }
      )
    );
  };

  const handleFileChange = (e, fieldName) => {
    const selectedFile = e.target.files[0];
    dispatch(
      UploadMediaRequestAPI(
        [selectedFile],
        "transportDocuments",
        false,
        (result) => {
      

          if (result.status) {
            setFormData((prev) => ({
              ...prev,
              [fieldName]: result.data[0].file_storage_id,
            }));
          } else {
            alert("File upload failed. Please try again.");
          }
        }
      )
    );
  };

  // Handle Add City Button click
  const handleAddCity = () => {
    if (
      !formData.selectedStateName ||
      !formData.gstin ||
      formData.selectedCities.length === 0
    ) {
      alert(
        "Please fill in all fields (State, GSTIN, and Cities) before adding."
      );
      return;
    }
    
   
    const cityNames = formData.selectedCities
      .map((city) => city.name)
      .join(", ");
    const newRow = {
      state: formData.selectedStateName,
      gstin: formData.gstin,
      cities: cityNames,

    };

    // setTableData((prevData) => [...prevData, newRow]);
    setTableData((prevData) => {
      if (editingIndex !== null) {
        // Update existing row
        const updatedData = [...prevData];

        updatedData[editingIndex] = newRow;
        return updatedData;
      } else {
        // Add new row
        return [...prevData, newRow];
      }
    });
    setFormData((prevData) => ({
      ...prevData,
      selectedCities: [], // Reset selected cities when new city is added
      gstin: "",
      selectedState: "",
      selectedStateName: "",
    }));
    setEditingIndex(null);
  };

  // Function to handle clicks outside the dropdown
  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsCityDropdownOpen(false); // Close the dropdown
    }
  };

  useEffect(() => {
    if (isCityDropdownOpen) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }

    // Cleanup event listener on unmount or when the dropdown state changes
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isCityDropdownOpen]);

  const handleEditClick = (index) => {
    const row = tableData[index]; // Get the selected row data
    const stateId = stateList.find((state) => state.name === row.state)?.id;

    if (stateId) {
      // Fetch city list based on stateId
      GetCity(countryId, stateId)
        .then((fetchedCities) => {
          // Set the city list after fetching
          setCityList(fetchedCities);

          // Now that the city list is updated, set form data
          setFormData({
            ...formData,
            selectedState: stateId || "",
            selectedStateName: row.state,
            gstin: row.gstin,
            selectedCities: row.cities.split(", ").map((cityName) => ({
              name: cityName,
              id: fetchedCities.find((city) => city.name === cityName)?.id, // Match the city name with ID
            })),
          });
        })
        .catch(console.log);
    }

    setEditingIndex(index);
  };

  const handleActionForm = () => {
    if (actionForm === "editForm") {
      // Logic for the "Update" action
      handleUpdate();
    } else {
      // Logic for the "Save" action
      handleSave();
    }
  };

  const handleSave = () => {
    const payload = {
      transport_name: formData.transportName,
      transport_head_office_city: formData.headOfficeCity,
      transport_service_number: formData.serviceNumber,
      transport_mobile_number: formData.mobileNumber,
      transport_alternate_mobile_number: formData.altMobileNumber,
      transport_service_number_certificate_storage_id: 778, // Update dynamically if needed
      transport_visiting_card_storage_id: 567, // Update dynamically if needed
      transport_state: tableData.map((stateData) => ({
        state_name: stateData.state,
        gst_number: stateData.gstin,
        transport_state_city: stateData.cities
          .split(", ")
          .map((city) => city.trim()),
        transport_state_gst_storage_id:
          formData.gstCertificates[stateData.state],
      })),
    };

    dispatch(
      addTransportAPI(payload, (result) => {
     
      })
    );
    return;
  };

  const handleUpdate = () => {
    const payload = {
      // transport_id:transportId,
      transport_name: formData.transportName,
      transport_head_office_city: formData.headOfficeCity,
      transport_service_number: formData.serviceNumber,
      transport_mobile_number: formData.mobileNumber,
      transport_alternate_mobile_number: formData.altMobileNumber,
      transport_service_number_certificate_storage_id: 778, // Update dynamically if needed
      transport_visiting_card_storage_id: 567, // Update dynamically if needed
      transport_state: tableData.map((stateData) => ({
        state_name: stateData.state,
        gst_number: stateData.gstin,
        transport_state_city: stateData.cities
          .split(", ")
          .map((city) => city.trim()),
        transport_state_gst_storage_id:
          formData.gstCertificates[stateData.state],
      })),
    }

    dispatch(updateTransportAPI(payload, (result)=>{
  
    } ))
  };
        
  return (
    <div className="transport-form">       
      <div className="upper-side-div-add-form">
        {/* Transport Name */}
        <div className="transport-name transport-input common-inp-class">
          <p>Enter Name of Transport</p>
          <input
            type="text"
            name="transportName"
            value={formData.transportName}
            placeholder="Enter transport name"
            onChange={handleInputChange}
          />
        </div>

        {/* Head Office City and Service Number */}
        <div className="input-office-city-service-number common-inp-class">
          <div className="transport-head-office-city">
            <p>Head Office City</p>
            <input
              type="text"
              name="headOfficeCity"
              value={formData.headOfficeCity}
              placeholder="Enter head office city"
              onChange={handleInputChange}
            />
          </div>
          <div className="transport-service-number">
            <p>Service Number</p>
            <input
              type="text"
              name="serviceNumber"
              value={formData.serviceNumber}
              placeholder="Enter service number"
              onChange={handleInputChange}
            />
          </div>
        </div>

        {/* Mobile Numbers */}
        <div className="transport-mob-num transport-input common-inp-class">
          <p>Enter Mobile Number</p>
          <input
            type="tel"
            name="mobileNumber"
            value={formData.mobileNumber}
            placeholder="Enter mobile number"
            onChange={handleInputChange}
          />
        </div>

        <div className="transport-alt-mob-num transport-input common-inp-class">
          <p>Enter Alternative Mobile Number</p>
          <input
            type="tel"
            name="altMobileNumber"
            value={formData.altMobileNumber}
            placeholder="Enter alternative mobile number"
            onChange={handleInputChange}
          />
        </div>

        {/* State and City */}
        <div className="transport-input common-inp-class">
          <p>Select State</p>
          <select
            name="selectedState"
            value={formData.selectedState}
            onChange={handleStateChange}
            style={{ cursor: "pointer" }}
          >
            <option value="">
              {" "}
              <p className="state-select-para">Select State</p>
            </option>
            {stateList.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>
        </div>

        {/* GSTIN */}
        <div className="transport-state-city-branches transport-input common-inp-class">
          <p>Enter GSTIN</p>
          <input
            type="text"
            name="gstin"
            value={formData.gstin}
            placeholder="Enter GSTIN"
            onChange={handleInputChange}
          />
        </div>

        {/* Cities Selection */}
        <div ref={dropdownRef} className="transport-input common-inp-class">
          <p>Select Cities</p>
          <div className="custom-dropdown">
            <button className="dropdown-btn" onClick={toggleCityDropdown}>
              {/* {formData.selectedCities.length > 0
                ? `Selected Cities (${formData.selectedCities.length})`
                : "Select Cities"} */}
              {formData.selectedCities.length > 0
                ? `Selected Cities (${formData.selectedCities
                    .map((city) => city.name)
                    .join(", ")})`
                : "Select Cities"}
            </button>
            {isCityDropdownOpen && (
              <div className="dropdown-content">
                {cityList.length > 0 ? (
                  cityList.map((city) => (
                    <div key={city.id} className="city-checkbox">
                      <input
                        className="city-inputt"
                        type="checkbox"
                        id={`city_${city.id}`}
                        value={city.id}
                        checked={formData.selectedCities.some(
                          (selectedCity) => selectedCity.id === city.id
                        )}
                        onChange={() =>
                          handleCityCheckboxChange(city.id, city.name)
                        }
                      />
                      <label htmlFor={`city_${city.id}`}>{city.name}</label>
                    </div>
                  ))
                ) : (
                  <p>No cities available for the selected state</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="button-div-transport">
          <button
            className="add-city-button"
            type="submit"
            onClick={handleAddCity}
          >
            Save
          </button>
        </div>
      </div>

      {/* Table */}
      {tableData?.length > 0 && (
        <div style={tableDataStyle.tableContainer}>
          <table border={1} style={tableDataStyle.table}>
            <thead>
              <tr>
                <th style={tableDataStyle.tableHeader}>State</th>
                <th style={tableDataStyle.tableHeader}>GSTIN</th>
                <th style={tableDataStyle.tableHeader}>Cities</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, index) => (
                <tr key={index}>
                  <td style={tableDataStyle.tableCell}>{row.state}</td>
                  <td style={tableDataStyle.tableCell}>{row.gstin}</td>
                  <td style={tableDataStyle.tableCell}>
                    {row.cities}{" "}
                    {actionForm == "editForm" && (
                      <img
                        onClick={() => handleEditClick(index)}
                        style={{
                          height: "16px",
                          marginLeft: "5px",
                          cursor: "pointer",
                        }}
                        src={Icons.editBlackIcon}
                        alt=""
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Lower section for file uploads */}
      <div className="lower-side-div-upload-doc">
        {tableData?.map((data, state) => (
          <div
            key={state}
            className="transport-gst-doc transport-input-doc common-inp-class"
          >
            <p>Upload {data?.state} GST Certificate *</p>
            <div
              className="custom-file-upload"
              onClick={() =>
                fileInputRef.current && fileInputRef.current.click()
              }
            >
              <input
                type="text"
                placeholder="Upload certificate"
                readOnly
                className="file-input-text"
              />
              <input
                type="file"
                ref={fileInputRef}
                className="file-input-hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileChangeState(e, data?.state)}
              />
              <button type="button" className="browse-button-transport">
                Browse
              </button>
            </div>
          </div>
        ))}

        <div className="transport-gst-doc transport-input-doc common-inp-class">
          <p>Upload Service number certificate *</p>
          <div
            className="custom-file-upload"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            <input
              type="text"
              placeholder="Upload certificate"
              readOnly
              className="file-input-text"
            />
            <input
              type="file"
              ref={fileInputRef}
              className="file-input-hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) =>
                handleFileChange(e, "serviceNumberCertificateFile")
              }
            />
            <button type="button" className="browse-button-transport">
              Browse
            </button>
          </div>
        </div>
        <div className="transport-gst-doc transport-input-doc common-inp-class">
          <p>Upload visiting card *</p>
          <div
            className="custom-file-upload"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            <input
              type="text"
              placeholder="Upload certificate"
              readOnly
              className="file-input-text"
            />
            <input
              type="file"
              ref={fileInputRef}
              className="file-input-hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => handleFileChange(e, "visitingCardFile")}
            />
            <button type="button" className="browse-button-transport">
              Browse
            </button>
          </div>
        </div>
      </div>

      <div className="btnn-div-transport">
        <div className="button-div-transport-last">
          <button
            onClick={handleActionForm}
            className="save-button-transport update-button-transport"
          >
            {actionForm == "addForm" ? "Save" : "Update"}
          </button>
        </div>
      </div>
    </div>
  );
};

const tableDataStyle = {
  tableContainer: {
    width: "90%",
    overflowX: "auto",
    marginTop: "20px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    border: "1px solid #ccc",
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    // backgroundColor: "#ffffff",
    color: "#333",
    fontWeight: "400",
    padding: "12px 20px",
    textAlign: "left",
    borderBottom: "2px solid #ddd",
  },
  tableRow: {
    borderBottom: "1px solid #eee",
  },
  tableCell: {
    padding: "12px 20px",
    backgroundColor: "#ffffff",
    fontSize: "16px",
    color: "#555",
    textAlign: "left",
  },
};

export default TransporterForm;